import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import {
  apiClient,
  setApiAuthToken,
  setUnauthorizedHandler,
  setRefreshTokenProvider,
  setTokensUpdatedHandler,
} from '@/lib/apiClient';

const AuthContext = createContext(null);
const TOKEN_STORAGE_KEY = 'token';
const REFRESH_STORAGE_KEY = 'refresh_token';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [effectivePlan, setEffectivePlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem(TOKEN_STORAGE_KEY));

  const applyTokens = useCallback((accessToken, refreshToken) => {
    if (accessToken) {
      localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
      setToken(accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(REFRESH_STORAGE_KEY, refreshToken);
    }
  }, []);

  const clearAuthState = useCallback(() => {
    setToken(null);
    setUser(null);
    setSubscription(null);
    setEffectivePlan(null);
    setLoading(false);
  }, []);

  const logout = useCallback(() => {
    const refreshToken = localStorage.getItem(REFRESH_STORAGE_KEY);
    if (refreshToken) {
      apiClient.post('/auth/logout', { refresh_token: refreshToken }).catch(() => {});
    }
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_STORAGE_KEY);
    clearAuthState();
    try {
      const channel = new BroadcastChannel('auth_channel');
      channel.postMessage({ type: 'logout' });
      channel.close();
    } catch (_) {}
  }, [clearAuthState]);

  useEffect(() => {
    setUnauthorizedHandler(() => logout());
    setRefreshTokenProvider(() => localStorage.getItem(REFRESH_STORAGE_KEY));
    setTokensUpdatedHandler(({ accessToken, refreshToken }) => {
      applyTokens(accessToken, refreshToken);
    });
  }, [logout, applyTokens]);

  useEffect(() => {
    setApiAuthToken(token);
  }, [token]);

  const fetchSubscription = useCallback(async () => {
    if (!token) return null;
    try {
      const response = await apiClient.get('/subscription/current');
      const sub = response.data?.subscription || null;
      setSubscription(sub);
      setEffectivePlan(response.data?.effective_plan || null);
      return sub;
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      setSubscription(null);
      return null;
    }
  }, [token]);

  const updateSubscription = useCallback((sub) => {
    setSubscription(sub);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
      await fetchSubscription();
    } catch (error) {
      console.error('Failed to fetch user:', error);
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [token, fetchSubscription, logout]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const response = await apiClient.post('/auth/login', { email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    applyTokens(access_token, refresh_token);
    setUser(userData);
    return userData;
  };

  const register = async (
    email,
    password,
    full_name,
    role = 'student',
    referral_code = null,
    tutor_code = null,
    preparation_mode = 'self',
    invite_code = null
  ) => {
    const response = await apiClient.post('/auth/register', {
      email,
      password,
      full_name,
      role,
      referral_code,
      tutor_code,
      invite_code,
      preparation_mode,
    });
    const { access_token, refresh_token, user: userData } = response.data;
    applyTokens(access_token, refresh_token);
    setUser(userData);
    try {
      const { trackEvent } = await import('@/lib/analytics');
      trackEvent('register', { role });
    } catch (_) {}
    return userData;
  };

  // OAuth-вход через Яндекс/VK. payload — то, что вернул провайдер (code и т.д.) + intent.
  const oauthLogin = async (provider, payload) => {
    const response = await apiClient.post(`/oauth/${provider}`, payload);
    const { access_token, refresh_token, user: userData } = response.data;
    applyTokens(access_token, refresh_token);
    setUser(userData);
    try {
      const { trackEvent } = await import('@/lib/analytics');
      trackEvent('login', { provider });
    } catch (_) {}
    return userData;
  };

  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const response = await apiClient.get('/auth/me');
        setUser(response.data);
        await fetchSubscription();
        return response.data;
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
    return null;
  }, [token, fetchSubscription]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== TOKEN_STORAGE_KEY) return;

      const nextToken = event.newValue;
      if (!nextToken) {
        clearAuthState();
        window.location.href = '/login';
        return;
      }

      setToken((currentToken) => {
        if (currentToken === nextToken) return currentToken;
        return nextToken;
      });
      setUser(null);
      setSubscription(null);
      setLoading(true);
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [clearAuthState]);

  useEffect(() => {
    let channel;
    try {
      channel = new BroadcastChannel('auth_channel');
      channel.onmessage = (event) => {
        if (event.data?.type === 'logout') {
          clearAuthState();
          window.location.href = '/login';
        }
      };
    } catch (_) {}
    return () => {
      try { channel?.close(); } catch (_) {}
    };
  }, [clearAuthState]);

  const hasActivePro = false;
  const hasPlusOrBetter = effectivePlan?.plan_id === 'plus' || effectivePlan?.plan_id === 'max';
  const planName = effectivePlan?.plan?.name || 'Старт';

  return (
    <AuthContext.Provider value={{
      user,
      subscription,
      effectivePlan,
      hasActivePro,
      hasPlusOrBetter,
      planName,
      loading,
      login,
      register,
      oauthLogin,
      logout,
      token,
      fetchUser,
      refreshUser,
      refreshSubscription: fetchSubscription,
      updateSubscription,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
